import React, { useEffect, useRef, useState } from 'react';

// ช่องวันที่แบบ วว/ดด/ปปปป (ปี ค.ศ.) ทุกเครื่อง (ช่องวันที่ของเบราว์เซอร์แสดงตามภาษาเครื่อง เช่น mm/dd/yyyy)
// ใช้แทน <input type="date"> ได้ทันที: value / onChange เป็น yyyy-mm-dd เหมือนเดิม
// แสดงปี ค.ศ. · พิมพ์ปี พ.ศ. (12/10/2569) ระบบแปลงเป็น ค.ศ. (12/10/2026) ให้เอง · หรือกด 📅 เลือกจากปฏิทิน
// มือถือ (iPhone ไม่มีปุ่ม /): พิมพ์ตัวเลขติดกันได้ เช่น 01102026 → 01/10/2026 (ใส่ / ให้เองขณะพิมพ์) · 011026 = 01/10/2026
// ใส่ / ไม่ครบ (01/012026) ก็จัดรูปให้ถูก (แก้ 22/10/2569 ตามภาพผู้ใช้)
// พิมพ์ผิด / ก่อนวันที่อนุญาต → ตัวเลขไม่หาย ขึ้นข้อความสีแดงใต้ช่องให้แก้
const toText = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || ''); return m ? `${m[3]}/${m[2]}/${m[1]}` : ''; };
const fmtDigits = (t) => { const d = t.replace(/\D/g, '').slice(0, 8); return d.length > 4 ? `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}` : d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d; };
// จัดรูปจากตัวเลขที่พิมพ์ — ใส่ / ครบ ไม่ครบ หรือไม่ใส่เลย ก็ได้ (01102026 · 01/102026 · 01/012026 · 0110/2026)
// ยกเว้นพิมพ์วัน/เดือนหลักเดียวพร้อม / เอง (1/10/2026 · 01/1/2026) → เก็บตามที่พิมพ์
const norm = (raw) => {
  const t = String(raw || '').replace(/\s/g, '');
  if (!/^[\d/.-]*$/.test(t)) return t;
  const segs = t.split(/[/.-]/);
  if (segs.slice(0, -1).some((x) => x.length === 1)) return t;
  return fmtDigits(t);
};
const parse = (s) => {
  const t = norm(String(s || '').trim());
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(t);
  if (!m) return null;
  let y = Number(m[3]); if (y < 100) y += 2000; if (y > 2400) y -= 543;
  if (y < 2000 || y > 2100) return null;   // ปีแปลก ๆ = พิมพ์ผิด
  const d = Number(m[1]), mo = Number(m[2]), dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

export default function DateInput({ value, onChange, style, disabled, min, max, title, placeholder, ...rest }) {
  const pick = useRef(null);
  const [text, setText] = useState(toText(value)), [err, setErr] = useState('');
  useEffect(() => { setText(toText(value)); setErr(''); }, [value]);
  const emit = (iso) => { setErr(''); if (onChange) onChange({ target: { value: iso } }); };
  const commit = (t) => {
    if (!String(t || '').trim()) { setErr(''); if (value) emit(''); return; }
    const iso = parse(t);
    if (!iso) { setErr('วันที่ไม่ถูกต้อง — พิมพ์ วว/ดด/ปปปป เช่น 01102026'); return; }
    if (min && iso < min) { setErr(`ต้องไม่ก่อน ${toText(min)}`); return; }
    if (max && iso > max) { setErr(`ต้องไม่หลัง ${toText(max)}`); return; }
    setText(toText(iso));
    if (iso !== value) emit(iso); else setErr('');
  };
  const typed = (raw) => {
    // พิมพ์เพิ่ม → จัดรูป วว/ดด/ปปปป ให้เอง (ใส่ / หรือไม่ใส่ก็ได้) · กำลังลบ → เก็บตามที่เห็น ไม่จัดรูปทับ
    const t = raw.length < text.length ? raw : norm(raw);
    setText(t); setErr('');
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(t)) commit(t);
  };
  // คอมพิวเตอร์: คลิกไอคอนแล้วเปิดปฏิทินทันที · มือถือ: แตะช่องปฏิทินของเครื่องเปิดเอง (ไม่ต้องสั่ง)
  const openPicker = () => { const el = pick.current; if (!el || disabled) return; try { if (el.showPicker) el.showPicker(); } catch (e) { /* มือถือเปิดเองอยู่แล้ว */ } };
  const { width, minWidth, flex, display, ...inner } = style || {};
  return <span style={{ display: display === 'block' ? 'flex' : 'inline-flex', flexDirection: 'column', width: width ?? '100%', minWidth: minWidth ?? 0, flex, boxSizing: 'border-box' }}>
    <span style={{ position: 'relative', display: 'flex', width: '100%' }}>
      <input {...rest} type="text" inputMode="numeric" autoComplete="off" placeholder={placeholder || 'วว/ดด/ปปปป'} value={text} disabled={disabled} title={title || 'วันที่ วว/ดด/ปปปป (ค.ศ.) · มือถือพิมพ์ตัวเลขติดกันได้ เช่น 01102026'}
        aria-invalid={err ? true : undefined}
        onChange={(e) => typed(e.target.value)}
        onBlur={(e) => commit(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') commit(e.currentTarget.value); }}
        style={{ ...inner, width: '100%', boxSizing: 'border-box', paddingRight: 40, fontSize: Math.max(16, parseFloat(inner.fontSize) || 16), ...(err ? { borderColor: '#d92d20', background: '#fff5f5' } : null) }} />
      <span aria-hidden="true" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 16, lineHeight: 1, pointerEvents: 'none', opacity: disabled ? 0.4 : 1 }}>📅</span>
      {/* ปฏิทินของเครื่องจริง (โปร่งใส) วางทับไอคอน 📅 — มือถือ iPhone / Android แตะแล้วเปิดปฏิทินของเครื่องได้เลย (เดิมเปิดผ่านปุ่ม มือถือบล็อก · แก้ 23/10/2569) */}
      <input ref={pick} type="date" value={value || ''} min={min} max={max} tabIndex={-1} disabled={disabled} aria-label="เลือกวันที่จากปฏิทิน" title="เลือกวันที่จากปฏิทิน"
        onChange={(e) => { if (e.target.value) emit(e.target.value); }} onClick={openPicker}
        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 38, height: '100%', opacity: 0, border: 0, padding: 0, margin: 0, fontSize: 16, cursor: disabled ? 'default' : 'pointer', zIndex: 1, boxSizing: 'border-box' }} />
    </span>
    {err && <span role="alert" style={{ fontSize: 11.5, color: '#d92d20', marginTop: 3, lineHeight: 1.3 }}>{err}</span>}
  </span>;
}
